<template>
  <div class="flex items-center justify-between gap-2">
    <template v-if="!showAsMenu">
      <Dropdown
        triggerStyle="p-2 hover:bg-surface-3 rounded-full"
        :close-on-outside-click="true"
        :closeOnSelect="true"
      >
        <template #trigger>
          <div class="relative">
            <EllipsisVerticalIcon class="text-ink-soft size-5" />
            <span
              v-if="
                can('can-resolve-comment') &&
                currentTab !== 'urgent_action_items' &&
                (order_item as DashboardItem).unresolved_comments_count > 0
              "
              :title="`${(order_item as DashboardItem).unresolved_comments_count} unresolved comment(s)`"
              class="dash-comment-badge"
            >
              {{ (order_item as DashboardItem).unresolved_comments_count }}
            </span>
          </div>
        </template>
        <template #contents>
          <ActionMenuItems
            :order-item="order_item"
            :current-tab="currentTab"
            @select-row="emits('selectRow', $event)"
            @open-details="emits('openDetails', $event)"
            @change-factory="(orderId, orderItemId) => changeFactory(orderId, orderItemId)"
            @view-pdf="(orderId) => getAwsURLFromOrder(orderId)"
            @open-provide-quote="provideQuoteModal = true"
            @notify-customer="(orderId) => notifyCustomerForQuote(orderId)"
            @delete-order="(orderId) => deleteOrder(orderId)"
            @price-breakdown="(orderItem) => priceBreakdown(orderItem)"
            @update-urgency="(type, id) => updateUrgency(type, id)"
            @fetch-pdf="(order, version) => fetchAndOpenPdf(order, version)"
            @upload-custom-techpack="(orderItem) => uploadCustomTechpack(orderItem)"
            @open-internal-note="internalNoteModal = true"
            @copy-order="(orderId) => copyOrder(orderId)"
          />
        </template>
      </Dropdown>
    </template>
    <template v-else-if="showAsMenu">
      <div
        class="bg-surface rounded-lg shadow-md w-max sticky z-[10000] overflow-hidden"
        ref="menuRef"
      >
        <ActionMenuItems
          :order-item="order_item"
          :current-tab="currentTab"
          @select-row="emits('selectRow', $event)"
          @open-details="emits('openDetails', $event)"
          @change-factory="(orderId, orderItemId) => changeFactory(orderId, orderItemId)"
          @view-pdf="(orderId) => getAwsURLFromOrder(orderId)"
          @open-provide-quote="provideQuoteModal = true"
          @notify-customer="(orderId) => notifyCustomerForQuote(orderId)"
          @delete-order="(orderId) => deleteOrder(orderId)"
          @price-breakdown="(orderItem) => priceBreakdown(orderItem)"
          @update-urgency="(type, id) => updateUrgency(type, id)"
          @fetch-pdf="(order, version) => fetchAndOpenPdf(order, version)"
          @upload-custom-techpack="(orderItem) => uploadCustomTechpack(orderItem)"
          @open-internal-note="internalNoteModal = true"
          @copy-order="(orderId) => copyOrder(orderId)"
        />
      </div>
    </template>
    <Teleport to="body">
      <ProvideQuoteModal
        @onQuoteProvided="onFetch(true)"
        v-if="provideQuoteModal"
        :row="order_item"
      />
      <ChangeFactoryDialog
        :currentTab="currentTab"
        @close="onFetch"
        @closeMenu="forceCloseMenu"
        v-if="changeFactoryModal"
        :order_item="order_item"
      />

      <PriceBreakdownModal
        :order="order_ref"
        :appUrl="appURL"
        :order_item="order_item"
        v-if="priceBreakdownModal"
        @close="handlePriceBreakDownModalCloseEvent"
        @openOrderPriceModal="onOpenOrderPriceModalFromBreakdown"
        @fetchOnSave="onFetch(true)"
        @passiveOrderDataRefreshed="onPassiveOrderDataRefreshed"
      />

      <Transition name="fade">
        <CustomTechPackModal
          v-if="customTechpackModal"
          :order="(order_item as DashboardItem).order"
          @close="handleCustomTechPackModalCloseEvent"
          @onFetch="onFetch"
        />
      </Transition>
      <Transition name="fade">
        <AddInternalNoteModal
          :order_item_id="(order_item as DashboardItem).id"
          :internal_comment="
            order_item.internal_comment ? JSON.parse(order_item.internal_comment) : null
          "
          v-if="internalNoteModal"
          @on-close="onFetch(true)"
          @on-cancel="
            () => {
              internalNoteModal = false;
              emits('closeMenu');
            }
          "
        />
      </Transition>
      <Transition name="fade">
        <CopyOrderModal
          :order="order_ref"
          v-if="openCopyModal"
          @close="() => (openCopyModal = false)"
          @save="onFetch"
        />
      </Transition>
    </Teleport>
  </div>
</template>
<script setup lang="ts">
import Dropdown from '@/components/Shared/Dropdown.vue';
import { can, logError, openV2Application } from '@/composables';
import { showToast } from '@/composables/swal';
import { getAwsURLFromOrder } from '@/composables/index';
import { http } from '@/interceptors/axiosRequestInterceptor';
import { DashboardItem, OrderDetail } from '@/models';
import { useMainStore } from '@/store';
import { EllipsisVerticalIcon } from '@heroicons/vue/24/solid';
import { storeToRefs } from 'pinia';
import { onBeforeUnmount, onMounted, ref, Teleport, watch } from 'vue';
import ActionMenuItems from './ActionMenuItems.vue';
import { shouldCloseContextMenu } from './contextMenuOutsideClick';
import AddInternalNoteModal from './Modals/AddInternalNoteModal.vue';
import ChangeFactoryDialog from './Modals/ChangeFactoryModal.vue';
import CopyOrderModal from './Modals/CopyOrderModal.vue';
import CustomTechPackModal from './Modals/CustomTechPackModal.vue';
import PriceBreakdownModal from './Modals/PriceBreakdownModal.vue';
import ProvideQuoteModal from './Modals/ProvideQuoteModal.vue';

type Urgency = 'vip' | 'urgent' | 'extra_attention';

const storage_url = import.meta.env.VITE_APP_FILE_URL;

const changeFactoryModal = ref<boolean>(false);
const provideQuoteModal = ref<boolean>(false);
const priceBreakdownModal = ref<boolean>(false);
const customTechpackModal = ref<boolean>(false);
const internalNoteModal = ref<boolean>(false);
const openCopyModal = ref<boolean>(false);
const menuRef = ref<HTMLElement | null>(null);
// Use props for showAsMenu
// const props = defineProps<{ order_item_list: any; currentTab: string; showAsMenu: boolean }>();

const emits = defineEmits([
  'onFetch',
  'selectRow',
  'openDetails',
  'closeMenu',
  'openOrderPriceModal',
]);
const { order_item_list, currentTab, showAsMenu } = defineProps<{
  order_item_list: any;
  currentTab: string;
  showAsMenu: boolean;
}>();

const { loading } = storeToRefs(useMainStore());
const appURL = import.meta.env.VITE_VUE2_APP_URL;
const order_item = ref({ ...order_item_list });
const order_ref = ref<OrderDetail>();

// Re-sync the local `order_item` snapshot whenever the parent passes a refreshed
// `order_item_list` (e.g. after `fetchOnSave` refetches the list following a
// factory update). Without this, fields like `factory_name` — which the
// "Update factory" modal shows as its per-product placeholder — stay stale until
// a full page reload. Mirrors `syncOrderItemFromProps` in OrderDetailActions.vue.
function syncOrderItemFromProps(): void {
  if (!order_item_list?.id) return;
  order_item.value = { ...order_item_list };
}

watch(() => order_item_list, syncOrderItemFromProps, { deep: true });
const {
  deleteOrder: DeleteFromStore,
  notifyCustomerForQuote,
  updateOrderUrgency,
  getOrderDetail,
} = useMainStore();

const updateUrgency = async (status: Urgency, orderId: number | string) => {
  await updateOrderUrgency(status, orderId);
  emits('selectRow', null);
  emits('onFetch', true);
};

const onFetch = async (fetch: boolean) => {
  changeFactoryModal.value = false;
  provideQuoteModal.value = false;
  internalNoteModal.value = false;
  // Notify the parent BEFORE any awaited work: when this component is the
  // right-click context menu, closeMenu unmounts it (v-if) and any emits
  // issued after an await would be lost — the dashboard would never refresh
  // (fetchOnSave / invalidateQueries). (PBI #20041)
  emits('onFetch', fetch);
  // Bug 20628 (Zendesk #8494) — saving inside the Price Breakdown modal routes through here, and
  // `closeMenu` unmounts the context-menu host together with its teleported modal, so a Save closed
  // the modal the user was still working in. Same hazard already handled in
  // `onPassiveOrderDataRefreshed`. The modal owns its own dismissal: when it really closes,
  // `handlePriceBreakDownModalCloseEvent` emits `closeMenu`.
  if (!priceBreakdownModal.value) {
    emits('closeMenu');
  }
  if (fetch && order_ref.value?.id) {
    const orderItemId = (order_item.value as { id?: number })?.id;
    try {
      await priceListData(order_ref.value.id, orderItemId);
    } catch {
      /* parent fetchOnSave still refreshes list + pane */
    }
  }
};
const deleteOrder = async (orderId: number): Promise<void> => {
  await DeleteFromStore(orderId);
  emits('selectRow', null);
  emits('onFetch', true);
};

const handlePriceBreakDownModalCloseEvent = (fetch_order_items = false) => {
  priceBreakdownModal.value = false;
  emits('selectRow', null);
  if (fetch_order_items) {
    emits('onFetch', fetch_order_items);
  }
  // Bug 20628 (Zendesk #8494) — `onFetch` no longer emits `closeMenu` while the modal is open, so the
  // menu is dismissed here, when the modal actually closes. This also fixes the context menu lingering
  // behind the modal after an explicit close (X), which never emitted `closeMenu` at all.
  emits('closeMenu');
};

const onOpenOrderPriceModalFromBreakdown = () => {
  priceBreakdownModal.value = false;
  emits('openOrderPriceModal');
};

/**
 * AB#20223 follow-up (BLOCKER 1) — the modal's passive, on-open background refresh notify. Mirrors
 * `onFetch`'s data-sync side effects (bubble a list/pane refresh, refresh this component's own
 * `order_ref`/`order_item` snapshot) but must NEVER emit `closeMenu`: when this component is the
 * right-click context menu, `closeMenu` unmounts it (v-if) — and the nested modal with it — which
 * self-closed the modal ~1s after a passive, no-user-action open.
 */
const onPassiveOrderDataRefreshed = async () => {
  emits('onFetch', true);
  if (order_ref.value?.id) {
    const orderItemId = (order_item.value as { id?: number })?.id;
    try {
      await priceListData(order_ref.value.id, orderItemId);
    } catch {
      /* the modal already re-hydrated its own state from its own background refetch */
    }
  }
};
// price list data get

const priceListData = async (orderId: number, orderItemId?: number) => {
  try {
    order_ref.value = await getOrderDetail(orderId, orderItemId);
    let order_item_total_price = 0; // Use `let` instead of `const`
    const factory_products = order_ref.value.items?.[0]?.factory_products ?? [];
    factory_products.forEach((product) => {
      order_item_total_price += product?.prices?.factory_product_total_price ?? 0;
    });
    order_item.value.factory_products = factory_products;
    order_item.value.order_item_total_price = order_item_total_price;
  } catch (error) {
    logError('Error fetching price list data:', error);
    return null;
  }
};

const priceBreakdown = async (orderItem: DashboardItem) => {
  await priceListData(orderItem.order.id, orderItem.id);
  priceBreakdownModal.value = true;
};

const uploadCustomTechpack = async (orderItem: DashboardItem) => {
  customTechpackModal.value = true;
};
const handleCustomTechPackModalCloseEvent = () => {
  customTechpackModal.value = false;
};

const changeFactory = async (orderId: number, orderItemId: number) => {
  await priceListData(orderId, orderItemId);
  changeFactoryModal.value = true;
};

const openCondensedTechpackFallback = (order_id: number, version_id: number | string, techpack_data_length: number) => {
  if (techpack_data_length) {
    openV2Application(
      { name: '', to: `/order/${order_id}/condensend-pdf?techpack_version=${version_id}` },
      true
    );
  } else {
    openV2Application({ name: '', to: `/order/${order_id}/condensend-pdf` }, true);
  }
};

/**
 * Poll the techpack URL endpoint (status-only, so it does NOT re-trigger regeneration) until the
 * backend reports `generating === false`, i.e. the Node service has written the fresh version URL
 * and cleared the flag. Bounded to stay well under the backend's stale-flag TTL.
 */
const pollForFreshTechpackPdf = async (order_id: number, version_id: number | string): Promise<string | null> => {
  const maxAttempts = 30; // ~90s at a 3s interval
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, 3000));
    try {
      const res = await http.get(`/techpackpdf/order/${order_id}/version/${version_id}`, {
        params: { status_only: 1 },
      });
      const { pdf_url, generating } = res.data.result;
      if (!generating) {
        return pdf_url ?? null;
      }
    } catch (error) {
      logError('Error polling techpack PDF status:', error);
      return null;
    }
  }
  return null; // timed out — still generating in the background
};

const fetchAndOpenPdf = async (order, version_id) => {
  const order_id = order.id;
  const techpack_data_length = order.techpack_data_count ? order.techpack_data_count : 0;
  try {
    loading.value = true;
    const response = await http.get(`/techpackpdf/order/${order_id}/version/${version_id}`);
    loading.value = false;
    let pdfUrl = response.data.result.pdf_url;
    const generating = response.data.result.generating;

    // Requesting a PDF regenerates it on the backend. When this platform triggered the regeneration,
    // wait for the fresh copy instead of opening a stale one; other platforms get the latest immediately.
    if (generating) {
      showToast('Techpack PDF is generating, please wait…', 'info');
      pdfUrl = await pollForFreshTechpackPdf(order_id, version_id);
      if (!pdfUrl) {
        showToast('Techpack PDF is still generating. Please try again in a moment.', 'info');
        return;
      }
    }

    if (pdfUrl) {
      window.open(`${storage_url}/${pdfUrl}`, '_blank');
    } else {
      openCondensedTechpackFallback(order_id, version_id, techpack_data_length);
    }
  } catch (error) {
    loading.value = false;
    logError('Error fetching the PDF URL:', error);
  }
};

const copyOrder = async (orderId: number) => {
  try {
    order_ref.value = await getOrderDetail(orderId);
    openCopyModal.value = true;
  } catch (error) {
    logError('Error fetching the order details:', error);
  }
};

// AB#21973 (Zendesk #9522) — the forms this menu opens are drawers since AB#21826;
// a mousedown inside one must not close the menu, which unmounts the drawer with it.
function handleOutsideClick(event: MouseEvent) {
  if (!showAsMenu) return;
  if (shouldCloseContextMenu(menuRef.value, event.target)) {
    emits('closeMenu');
  }
}
function forceCloseMenu() {
  if (showAsMenu) {
    emits('closeMenu');
  }
}
onMounted(() => {
  document.addEventListener('mousedown', handleOutsideClick);
});
onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleOutsideClick);
});
</script>
<style lang="scss" scoped>
/*
  AB#21245 — the unresolved-comments badge.

  It was pushed -top-4 / -right-3 off a 20px icon — rem offsets against this
  app's 12.8px root font, so it floated a badge's width clear of the kebab and
  read as belonging to the row above. With the Actions column narrowed to the
  kebab it had nowhere to float to at all.

  Now it sits on the icon's top-right corner, sized in pixels, overlapping just
  enough to read as attached to the control it counts.
*/
.dash-comment-badge {
  position: absolute;
  top: -5px;
  right: -6px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  background: #f0a202;
  color: #241700;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  border: 1.5px solid #fff;
  pointer-events: none;
}
</style>
